import React, { useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { useDispatch, useSelector } from 'react-redux';
import {
  getEventFailureTicketsStatusAsync,
  selectEventFailureTicketsError,
  selectEventFailureTicketsJobId,
  selectEventFailureTicketsPolling,
  selectEventFailureTicketsResult,
  selectEventFailureTicketsStarting,
  selectEventFailureTicketsStatus,
  startEventFailureTicketsJobAsync,
  stopEventFailureTicketsPolling,
} from '../../../redux/eventFailureTickets/eventFailureTicketsSlice';
import EventFailureTicketsDialog from './EventFailureTicketsDialog';
import { eventFailureTicketsStyles } from './styles';

const REQUIRED_FIELDS = [
  ['regression_test_id', 'Regression Test ID'],
  ['market_id', 'Market'],
  ['device_id', 'Device'],
];

const POLL_INTERVAL_MS = 4000;

export default function EventFailureTicketsButton({ searchFilters = {} }) {
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);

  const jobId = useSelector(selectEventFailureTicketsJobId);
  const status = useSelector(selectEventFailureTicketsStatus);
  const response = useSelector(selectEventFailureTicketsResult);
  const error = useSelector(selectEventFailureTicketsError);
  const starting = useSelector(selectEventFailureTicketsStarting);
  const polling = useSelector(selectEventFailureTicketsPolling);

  const missingFields = REQUIRED_FIELDS.filter(([key]) => !searchFilters?.[key]).map(
    ([, label]) => label,
  );

  const payload = useMemo(
    () => ({
      regression_test_id: searchFilters?.regression_test_id,
      market_id: Number(searchFilters?.market_id),
      device_id: Number(searchFilters?.device_id),
    }),
    [searchFilters],
  );

  const loading = starting || polling;
  const disabled = loading || missingFields.length > 0;
  const tooltipTitle = missingFields.length
    ? `Requires ${missingFields.join(', ')} in the current search`
    : 'Create Jira tickets for event failures';

  const handleCreateTickets = async () => {
    if (disabled) return;

    setDialogOpen(true);
    const result = await dispatch(startEventFailureTicketsJobAsync(payload));

    if (result?.success && result?.data?.job_id) {
      dispatch(getEventFailureTicketsStatusAsync(result.data.job_id));
    }
  };

  useEffect(() => {
    if (!polling || !jobId) return undefined;

    const interval = setInterval(() => {
      dispatch(getEventFailureTicketsStatusAsync(jobId));
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [dispatch, jobId, polling]);

  useEffect(
    () => () => {
      dispatch(stopEventFailureTicketsPolling());
    },
    [dispatch],
  );

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span>
          <Button
            variant="outlined"
            color="warning"
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : <ConfirmationNumberIcon />
            }
            onClick={handleCreateTickets}
            disabled={disabled}
            sx={eventFailureTicketsStyles.button}
          >
            {starting ? 'Creating...' : polling ? 'Checking...' : 'Raise Jira Tickets'}
          </Button>
        </span>
      </Tooltip>

      <EventFailureTicketsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        payload={payload}
        searchFilters={searchFilters}
        jobId={jobId}
        status={status}
        polling={polling}
        starting={starting}
        response={response}
        error={error}
      />
    </>
  );
}
